import React from 'react'
import TruncatedText from '@components/common/TruncatedText/TruncatedText.component'
import type {
  HygieneVisitRow,
  OutstandingBalancePatientRow,
  ProductionByProviderRow,
} from '@services/api/dashboard/finance.api'
import { formatAppointmentId } from '@utils/billing.utils'
import {
  formatCurrency,
  formatHygieneVisitDate,
  formatProcedureLogsServices,
} from '@utils/dashboardFinance.util'

export type HygieneVisitDetailTableRow = HygieneVisitRow & {
  chartNumber: string
  appointmentId: string
  procedure_logs: HygieneVisitRow['procedure_logs']
  total: number
  rebooked?: string
}

export const OUTSTANDING_BALANCE_API_COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    linkToPatientProfile: true,
  },
  {
    key: 'charges',
    label: 'Charges',
    sortable: true,
    formatter: (_: unknown, row: OutstandingBalancePatientRow) =>
      formatCurrency(row.charges),
  },
  {
    key: 'payments',
    label: 'Payments',
    sortable: true,
    formatter: (_: unknown, row: OutstandingBalancePatientRow) =>
      formatCurrency(row.payments),
  },
  {
    key: 'balance',
    label: 'Balance',
    sortable: true,
    formatter: (_: unknown, row: OutstandingBalancePatientRow) =>
      formatCurrency(row.balance),
  },
]

export const HYGIENE_VISIT_DETAIL_TABLE_COLUMNS = [
  { key: 'chartNumber', label: 'Chart #', sortable: true },
  {
    key: 'patientName',
    label: 'Patient name',
    sortable: true,
    linkToPatientProfile: true,
  },
  {
    key: 'appointmentId',
    label: 'Appointment ID',
    sortable: true,
    formatter: (_: unknown, row: HygieneVisitDetailTableRow) =>
      formatAppointmentId(row.appointmentId),
  },
  { key: 'providerName', label: 'Provider', sortable: true },
  {
    key: 'visitDate',
    label: 'Visit date',
    sortable: true,
    formatter: (_: unknown, row: HygieneVisitDetailTableRow) =>
      formatHygieneVisitDate(row.visitDate),
  },
  {
    key: 'procedure_logs',
    label: 'Services',
    sortable: true,
    formatter: (_: unknown, row: HygieneVisitDetailTableRow) => (
      <TruncatedText text={formatProcedureLogsServices(row.procedure_logs)} />
    ),
  },
  {
    key: 'total',
    label: 'Production',
    sortable: true,
    formatter: (_: unknown, row: HygieneVisitDetailTableRow) =>
      formatCurrency(row.total),
  },
  { key: 'rebooked', label: 'Rebooked', sortable: true },
]

const HYGIENE_VISIT_SERVER_SORT_KEYS = new Set([
  'visitDate',
  'patientName',
  'providerName',
  'total',
])

export const HYGIENE_VISIT_BACKEND_TABLE_COLUMNS =
  HYGIENE_VISIT_DETAIL_TABLE_COLUMNS.map((column) => ({
    ...column,
    sortable: HYGIENE_VISIT_SERVER_SORT_KEYS.has(column.key),
  }))

export const PRODUCTION_PER_PROVIDER_API_COLUMNS = [
  { key: 'provider', label: 'Provider', sortable: true },
  { key: 'credential', label: 'Credential', sortable: true },
  {
    key: 'total_procedures',
    label: 'Total procedures',
    sortable: true,
    formatter: (_: unknown, row: ProductionByProviderRow) =>
      String(row.total_procedures ?? '—'),
  },
  {
    key: 'total_production',
    label: 'Production',
    sortable: true,
    formatter: (_: unknown, row: ProductionByProviderRow) =>
      formatCurrency(row.total_production),
  },
]
